/* « Étiquettes » — internal markers on campaigns and supports (ruling « Organisation », 19/10; Technical's paper 0036 and
   its ruling P1–P8). An étiquette is the merchant's own bookmark: never shown to a customer, never a rule of the parcours,
   never in a document, a version, /d, play or a till answer, and its name never speaks of the gift.
   lib-pack-21 · the wire. With an API (VITE_API_URL) the list is the server's (GET /v1/app/tags, read with the mirror at the
   app's root), each change of it one whole-list PUT (lib/writes › sendTags: a rename, a creation, « Supprimer »), « Fusionner »
   one POST …/tags/:id/merge, and an item's étiquettes are its row's (`Campaign.tags`, `SupportRef.tags`, contract 05 §5.4 and
   06 §6.1), written with PATCH { tags } on the item — row data, never a document, never a version. The tab's copy changes at
   once (lib/mirror › tags, itemTags) and the server's answer takes its place; a refusal reads the server's back and is said
   by the page (toastWhenSaved) or at the root (ServerWrites). The screens call the same names in both modes.
   The demo (no VITE_API_URL) keeps the list AND each item's étiquettes in one client store per world (`bmr.tags.<world>`),
   as the V1 LinkAsk store did, and calls nothing. The worlds' examples (cruising, nine-locations) seed that store, and the
   mock server's own database (mocks/server-b › seedTags), so the demo and the API mode of one world read the same page. */
import { useSyncExternalStore } from 'react'
import { currentWorld, type World } from '@/mocks/worlds'
import { apiWrites } from './api'
import { GIFT_WORDS, INCENTIVE_WORDS } from './asks'
import { edited, mirror, serverIdOf, setMirror, subscribeMirror } from './mirror'
import { currentUser, type CurrentUser } from './scope'

/** One étiquette of the workspace's list (the API's `tags` row). `counts` are the server's (P3: the reader's scope, archived
    campaigns and deactivated supports included) and travel only with an API; the demo counts its store (tagCounts). */
export interface Tag { id: string; name: string; position: number; createdAt: string; counts?: { campaigns: number; supports: number } }
/** What carries étiquettes (the API's campaign_tags / display_tags). */
export type TagKind = 'campaign' | 'support'
/** The ruling's cap: 30 étiquettes per workspace (PUT → too_many_items). */
export const MAX_TAGS = 30
/** P7: 5 étiquettes at most on a campaign or a support (PATCH → too_many_tags). */
export const MAX_PER_ITEM = 5
/** A name holds 1–24 characters after trim and inner whitespace collapsed (invalid_field). */
export const TAG_NAME_MAX = 24

/* the merchant's words for each refusal (⟦confirmable⟧, D-82) */
export const TAG_NAME_TIES_GIFT = 'Le nom de l’étiquette ne parle pas du cadeau.'
export const TAG_NAME_EMPTY = 'Donnez un nom à l’étiquette.'
export const TAG_NAME_LONG = `Un nom d’étiquette tient en ${TAG_NAME_MAX} caractères.`
export const TAG_NAME_TAKEN = 'Une étiquette porte déjà ce nom.'
export const TAG_CAP_LINE = `${MAX_TAGS} étiquettes au plus : renommez, fusionnez ou supprimez-en une pour en créer une autre.`
export const TAG_PER_ITEM_LINE = `${MAX_PER_ITEM} étiquettes au plus par élément : retirez-en une pour en choisir une autre.`
/** The API's own line for a role that cannot write the list (0036; lib/writes says it with « rien n’a changé »). */
export const TAG_ROLE_LINE = 'Votre rôle ne permet pas de gérer les étiquettes.'

export interface TagStore { items: Tag[]; campaigns: Record<string, string[]>; supports: Record<string, string[]> }
const key = (w: World = currentWorld()) => `bmr.tags.${w}`

/* ---------- the worlds' examples: cruising and nine-locations show a populated list; the others start empty ---------- */
const SEED_AT = '2026-10-01T09:00:00.000Z'
const EMPTY_STORE: TagStore = { items: [], campaigns: {}, supports: {} }
/** The examples a world starts with, under the demo's ids (the mock server re-keys them as it names its rows). */
export function tagExamples(w: World): TagStore {
  if (w !== 'cruising' && w !== 'nine-locations') return EMPTY_STORE
  const items: Tag[] = [
    { id: 'tag-automne', name: 'Automne 2026', position: 0, createdAt: SEED_AT },
    { id: 'tag-vitrine', name: 'Vitrine', position: 1, createdAt: SEED_AT },
    { id: 'tag-fidelisation', name: 'Fidélisation', position: 2, createdAt: SEED_AT },
  ]
  return {
    items,
    campaigns: { c1: ['tag-automne'], c3: ['tag-automne', 'tag-fidelisation'], c5: ['tag-vitrine'] },
    supports: { s1: ['tag-automne'], s2: ['tag-vitrine'], ...(w === 'nine-locations' ? { s6: ['tag-vitrine'] } : {}) },
  }
}

/* ---------- the demo's store (one per world, in this browser) ---------- */
/* one parsed snapshot per (world, stored string), so useSyncExternalStore gets a stable value */
let cache: { k: string; raw: string | null; v: TagStore } | null = null
function readWorld(): TagStore {
  const k = key()
  let raw: string | null = null
  try { raw = localStorage.getItem(k) } catch { /* fine */ }
  if (cache && cache.k === k && cache.raw === raw) return cache.v
  let v: TagStore | null = null
  if (raw) { try { const p = JSON.parse(raw) as TagStore; if (p && Array.isArray(p.items)) v = { items: p.items, campaigns: p.campaigns ?? {}, supports: p.supports ?? {} } } catch { /* fine */ } }
  cache = { k, raw, v: v ?? tagExamples(currentWorld()) }
  return cache.v
}
const listeners = new Set<() => void>()
function writeWorld(s: TagStore): void {
  try { localStorage.setItem(key(), JSON.stringify(s)) } catch { /* fine */ }
  listeners.forEach(l => l())
}
const subscribeWorld = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }

/* ---------- with an API: the tab's copy of the server's (lib/mirror) ---------- */
/* one store object per (tags, itemTags) pair of the mirror, so useSyncExternalStore gets a stable value */
let apiCache: { items: Tag[]; it: { campaigns: Record<string, string[]>; supports: Record<string, string[]> }; v: TagStore } | null = null
function readApi(): TagStore {
  const m = mirror()
  if (apiCache && apiCache.items === m.tags && apiCache.it === m.itemTags) return apiCache.v
  apiCache = { items: m.tags, it: m.itemTags, v: { items: m.tags, campaigns: m.itemTags.campaigns, supports: m.itemTags.supports } }
  return apiCache.v
}
/** The tab's copy changes at once; what changed is said to lib/writes by the seam that changed it (`edited`). */
const writeApi = (s: TagStore): void => setMirror({ tags: s.items, itemTags: { campaigns: s.campaigns, supports: s.supports } })

const read = (): TagStore => (apiWrites ? readApi() : readWorld())
const write = (s: TagStore): void => (apiWrites ? writeApi(s) : writeWorld(s))
const subscribe = (l: () => void) => (apiWrites ? subscribeMirror(l) : subscribeWorld(l))
/** An item's key in the store: with an API the server's id (a draft named here, then by the server, reads as one). */
const keyOf = (id: string): string => (apiWrites ? serverIdOf(id) : id)

/** P2 · the key a name is unique by: trim, inner whitespace collapsed, NFC, `toLocaleLowerCase('fr')` — case folds,
    accents stay significant (« Noël » ≠ « Noel »). */
export const normTagName = (name: string): string => cleanTagName(name).toLocaleLowerCase('fr')
/** The name as it is stored and shown: trimmed, inner whitespace collapsed, NFC. */
export const cleanTagName = (name: string): string => name.trim().replace(/\s+/g, ' ').normalize('NFC')
/** How long a name is, as the API counts it (`[...name].length`: code points, so « 🎁 » is one). */
const nameLength = (name: string): number => [...name].length

/** The list, in position order. */
export const getTags = (): Tag[] => read().items.slice().sort((a, b) => a.position - b.position)
/** The whole list at once (the API's PUT): positions renumbered in the order given; an id dropped = « Supprimer »,
    its items lose it. With an API the tab's copy changes now and one PUT sends it (lib/writes › sendTags). */
export function setTags(list: Tag[]): void {
  const s = read()
  const items = list.map((t, i) => ({ ...t, name: cleanTagName(t.name), position: i }))
  const keep = new Set(items.map(t => t.id))
  const prune = (m: Record<string, string[]>) => Object.fromEntries(Object.entries(m).map(([id, ids]) => [id, ids.filter(x => keep.has(x))]))
  write({ items, campaigns: prune(s.campaigns), supports: prune(s.supports) })
  edited({ kind: 'tags' })
}
/** « Fusionner » (POST /v1/app/tags/:id/merge { into }): every item of `from` carries `into` instead — an item that had
    both keeps one — then `from` goes; the survivor keeps its name and position. Never takes an item over 5. */
export function mergeTag(from: string, into: string): void {
  if (from === into) return
  const s = read()
  if (!s.items.some(t => t.id === from) || !s.items.some(t => t.id === into)) return
  const repoint = (m: Record<string, string[]>) => Object.fromEntries(Object.entries(m).map(([id, ids]) =>
    [id, ids.includes(from) ? [...new Set(ids.map(x => (x === from ? into : x)))] : ids]))
  const items = s.items.filter(t => t.id !== from).sort((a, b) => a.position - b.position).map((t, i) => ({ ...t, position: i }))
  write({ items, campaigns: repoint(s.campaigns), supports: repoint(s.supports) })
  edited({ kind: 'tag-merge', from, into })
}
/** Why this name cannot be saved, in the merchant's words — or null. `selfId` is the étiquette being renamed (its own
    name is not a duplicate; a rename never meets the cap). Mirrors the API's invalid_field · duplicate_name ·
    name_ties_gift · too_many_items. */
export function tagIssues(name: string, list: Tag[] = getTags(), selfId?: string): string | null {
  const clean = cleanTagName(name)
  if (!clean) return TAG_NAME_EMPTY
  if (nameLength(clean) > TAG_NAME_MAX) return TAG_NAME_LONG
  if (GIFT_WORDS.test(clean) || INCENTIVE_WORDS.test(clean)) return TAG_NAME_TIES_GIFT
  if (list.some(t => t.id !== selfId && normTagName(t.name) === normTagName(clean))) return TAG_NAME_TAKEN
  if (!selfId && list.length >= MAX_TAGS) return TAG_CAP_LINE
  return null
}
/** A new étiquette's id (the app mints ids, as the library; the API takes `[A-Za-z0-9_-]{3,40}`). */
export const newTagId = () => `tag-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
/** Adds one étiquette at the end of the list; returns it, or the refusal line. */
export function addTag(name: string): Tag | string {
  const list = getTags()
  const issue = tagIssues(name, list)
  if (issue) return issue
  const t: Tag = { id: newTagId(), name: cleanTagName(name), position: list.length, createdAt: new Date().toISOString() }
  setTags([...list, t])
  return t
}

/** An item's étiquettes, in the list's order. The store holds them; `row.tags` (the API's field on a row the screen
    already has) is read when the store holds nothing for the item. Unknown ids are dropped. */
export function itemTags(kind: TagKind, id: string, row?: { tags?: string[] }): Tag[] {
  const s = read()
  const m = kind === 'campaign' ? s.campaigns : s.supports
  const ids = m[keyOf(id)] ?? m[id] ?? row?.tags ?? []
  return getTags().filter(t => ids.includes(t.id))
}
/** Replaces an item's étiquettes (PATCH { tags }): row data, never a version — a campaign gets no pending change.
    Returns the refusal line above 5, else null. With an API the tab's copy changes now and lib/writes sends it once the
    item is on the server (a draft not saved yet carries them in its first POST instead). */
export function setItemTags(kind: TagKind, id: string, ids: string[]): string | null {
  const uniq = [...new Set(ids)]
  if (uniq.length > MAX_PER_ITEM) return TAG_PER_ITEM_LINE
  const s = read()
  const known = new Set(s.items.map(t => t.id))
  const next = uniq.filter(x => known.has(x))
  const k = keyOf(id)
  const m = kind === 'campaign' ? s.campaigns : s.supports
  if ((m[k] ?? []).join() === next.join()) return null   /* nothing changes: nothing is written, nothing is sent */
  write(kind === 'campaign' ? { ...s, campaigns: { ...s.campaigns, [k]: next } } : { ...s, supports: { ...s.supports, [k]: next } })
  edited({ kind: 'item-tags', item: kind, id: k })
  return null
}
/** P8 · a copy keeps its organisation: the duplicate carries the source's étiquettes. `quiet` copies them in this tab
    only (lib/campaign › duplicateCampaign): with an API the copy's first POST then carries them (lib/server › saveDraft),
    one write instead of a POST and a PATCH. */
export function copyItemTags(kind: TagKind, from: string, to: string, quiet = false): void {
  const ids = itemTags(kind, from).map(t => t.id)
  if (!ids.length) return
  if (!quiet) { setItemTags(kind, to, ids); return }
  const s = read(); const k = keyOf(to)
  write(kind === 'campaign' ? { ...s, campaigns: { ...s.campaigns, [k]: ids } } : { ...s, supports: { ...s.supports, [k]: ids } })
}
/** The ids an item carries in this tab, for a write that sends them (a draft's first POST). */
export const itemTagIds = (kind: TagKind, id: string): string[] => itemTags(kind, id).map(t => t.id)
/** With an API, what the server holds of an item (a row read, a row answered): the tab's copy, unless a write asked
    since will send the tab's own (lib/writes checks that before noting an answer). `quiet`: a list read during a render
    (lib/campaign › mergeSupports) — it notes an item the tab has not seen yet and never overrides what the tab holds,
    so a support just made keeps the étiquettes on their way to the server. */
export function noteItemTags(kind: TagKind, id: string, ids: string[], quiet = false): void {
  if (!apiWrites) return
  const s = readApi()
  const m = kind === 'campaign' ? s.campaigns : s.supports
  if (quiet && id in m) return
  if (m[id] && m[id].join() === ids.join()) return
  const it = kind === 'campaign' ? { campaigns: { ...s.campaigns, [id]: ids }, supports: s.supports } : { campaigns: s.campaigns, supports: { ...s.supports, [id]: ids } }
  setMirror({ itemTags: it }, quiet)
}
/** A draft the server has just named: its étiquettes read under the server's id now (lib/campaign › rekeyCampaign). */
export function rekeyItemTags(from: string, to: string): void {
  if (!apiWrites || from === to) return
  const s = readApi()
  if (!(from in s.campaigns) || to in s.campaigns) return
  const { [from]: ids, ...rest } = s.campaigns
  setMirror({ itemTags: { campaigns: { ...rest, [to]: ids }, supports: s.supports } }, true)
}
/** P3 · how many items carry each étiquette, among the items given (the reader's scope; archived campaigns and
    deactivated supports included — the number « Supprimer » and « Fusionner » act on). With an API the server counts
    (GET /v1/app/tags › counts: every item in the reader's scope, the archived ones the app never lists included). */
export function tagCounts(campaignIds: string[], supportIds: string[]): Record<string, { campaigns: number; supports: number }> {
  const s = read()
  if (apiWrites) return Object.fromEntries(s.items.map(t => [t.id, t.counts ?? { campaigns: 0, supports: 0 }]))
  const out: Record<string, { campaigns: number; supports: number }> = Object.fromEntries(s.items.map(t => [t.id, { campaigns: 0, supports: 0 }]))
  for (const id of new Set(campaignIds)) for (const t of s.campaigns[id] ?? []) if (out[t]) out[t].campaigns++
  for (const id of new Set(supportIds)) for (const t of s.supports[id] ?? []) if (out[t]) out[t].supports++
  return out
}
/** Who writes the list (create, rename, merge, delete): the owner, an administrator, a manager over every
    establishment — the library's rule (mocks/handlers `coversAll`); a partial manager and staff read. An item's own
    étiquettes follow that item's write rights (routes/campagnes/roles `canBuild`). */
export function canWriteTagList(locationIds: string[], u: CurrentUser = currentUser()): boolean {
  return u.role === 'owner' || u.role === 'admin' || (u.role === 'manager' && locationIds.every(l => u.locations.includes(l)))
}
/** The store as a React value: re-renders on every write in this tab (and, with an API, on every answer read back). */
export function useTags(): Tag[] {
  const s = useSyncExternalStore(subscribe, read, read)
  return s.items.slice().sort((a, b) => a.position - b.position)
}
/** Re-renders a component when any item's étiquettes change (the lists' chips and filters). */
export const useTagStore = (): unknown => useSyncExternalStore(subscribe, read, read)
